import React, { useEffect, useState } from "react";
import { ContainerPanel } from "../container-panel/container-panel";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { ContainerPane } from "../container-pane/container-pane";
import { useI18N } from "@src/language/language-context";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { Button } from "@src/components/atoms/buttons/button/button";
import { useDispatch, useSelector } from "react-redux";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import {
  ZitadelUser,
  UserManagementListActiveRoleEnum,
  UserManagementListActiveRoleRenderDataType,
} from "@src/types/user-management";

import Spinner from "@src/components/atoms/spinner/spinner";
import { UsersList } from "../users-list/users-list";
import { UserManagementPageSearchBar } from "../user-management-page-search/user-management-page-searchbar";

import styles from "./users-list-view.module.scss";

import { getUsersUseCase } from "@src/cloud-connect/user-manager/use-cases/get-users-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { RootState } from "@src/reducers/store";
const cssClassName = "users-list-view";

interface UsersListViewProps {
  usersListData?: ZitadelUser[];
  userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType;
  onUsersLoaded?: (users: ZitadelUser[]) => void;
  onRenderDataChange: (
    userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType,
  ) => void;
}

export const UsersListView = ({
  usersListData,
  userManagementListActiveRoleData,
  onUsersLoaded,
  onRenderDataChange,
}: UsersListViewProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();

  const isUserListResultsReady = useSelector(
    (state: RootState) => state.yourSlice.userListResultsReady,
  );

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const [isLoading, setIsLoading] = useState(true);

  const texts = {
    subtitle: i18n("modules.usersListView.subtitle"),
    addNewUserButton: i18n("modules.usersListView.addNewUserButton"),
    disabledAddNewUserButtonTooltip: i18n(
      "modules.usersListView.disabledAddNewUserButtonTooltip",
    ),
    errorLoadingUsersText: i18n("modules.usersListView.errorLoadingUsersText"),
  };

  const handleAddNewUser = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "addUserDrawerModal",
      }),
    );
  };

  const loadUsers = async (signal: AbortSignal) => {
    const result = await getUsersUseCase(signal);
    if (signal?.aborted) return;

    if (result.success && result.data) {
      onUsersLoaded?.(result.data);
    } else {
      console.error("error loading users:", result.error);
      errorSnackbar(texts.errorLoadingUsersText, 5000);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setIsLoading(true);
    loadUsers(signal).finally(() => {
      if (!signal?.aborted) setIsLoading(false);
    });

    return () => {
      controller.abort();
    };
  }, [isUserListResultsReady]);

  return (
    <ContainerPanel>
      <SubHeader>
        <FlexRowContainer justifyContent="space-between">
          <SubTitle text={texts.subtitle} />
          {authContext?.permissions?.includes("manage-human-users") && (
            <Button text={texts.addNewUserButton} fun={handleAddNewUser} />
          )}
        </FlexRowContainer>
      </SubHeader>
      <ContainerPane>
        <UserManagementPageSearchBar
          userManagementListActiveView={UserManagementListActiveRoleEnum.USERS}
          userManagementListActiveRoleData={userManagementListActiveRoleData}
          onRenderDataChange={onRenderDataChange}
        />
        {isLoading ? (
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <Spinner />
          </div>
        ) : (
          <UsersList users={usersListData} />
        )}
      </ContainerPane>
    </ContainerPanel>
  );
};
